// client/src/pages/sections/groups/GroupAnchorPicker.js
// Searchable, scrollable picker over a list of group anchors (SG or RG).
// Replaces the native <datalist> in SplitMemberButton / MoveToRgButton so
// we can:
//   • position the suggestion list directly under the input
//   • scroll inside a fixed-height list rather than relying on the
//     browser's datalist UI
//   • require a minimum query length before showing options
//
// Props:
//   anchors      [{ clientCode, clientName, members?: [{ clientCode, clientName }] }] — members make the group findable by
//                any client in it; a pick always commits the ANCHOR
//   value        controlled string (typically `${code} — ${name}` after select)
//   onChange     (newValue) ⇒ void  — fires on every keystroke + on select
//   onFocus      forwarded to the input
//   disabled
//   placeholder
//   minChars     default 3
//   maxResults   default 50

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ClientChip from './ClientChip';

export default function GroupAnchorPicker({
  anchors,
  value,
  onChange,
  onFocus,
  disabled = false,
  placeholder = 'Start typing code or name…',
  minChars = 3,
  maxResults = 50,
  inputStyle,
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);
  // position:fixed coords so the dropdown can escape modal overflow:hidden.
  // dropUp flips the list ABOVE the input when there's not enough room below
  // (tall modals near the bottom of the viewport would otherwise clip it).
  const [popPos, setPopPos] = useState({ left: 0, top: 0, bottom: 0, width: 320, dropUp: false });

  const query = (value || '').trim().toLowerCase();
  const showHint = query.length > 0 && query.length < minChars;

  const matches = useMemo(() => {
    if (query.length < minChars) return [];
    const out = [];
    for (const a of anchors) {
      const code = a.clientCode || '';
      const name = a.clientName || '';
      if (
        code.toLowerCase().includes(query) ||
        name.toLowerCase().includes(query)
      ) {
        out.push(a);
        if (out.length >= maxResults) break;
      }
    }
    // A group can also be found by any client IN it (a.members, from dgGroupAnchors): a group that was re-anchored no
    // longer lists its old anchor, and partners search by the client they know. Such a hit is the GROUP — picking it
    // commits the anchor — and shows which client matched (`via`). Direct anchor hits stay first.
    if (out.length < maxResults) {
      const direct = new Set(out.map(a => a.clientCode));
      for (const a of anchors) {
        if (direct.has(a.clientCode) || !Array.isArray(a.members)) continue;
        const hit = a.members.find(m =>
          (m.clientCode || '').toLowerCase().includes(query) ||
          (m.clientName || '').toLowerCase().includes(query));
        if (hit) {
          out.push({ ...a, via: hit });
          if (out.length >= maxResults) break;
        }
      }
    }
    return out;
  }, [anchors, query, minChars, maxResults]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Reset highlight when matches change.
  useEffect(() => { setHighlight(0); }, [query]);

  // Pin the dropdown to the input's bounding rect (position:fixed). We
  // re-measure on open, on every render while open, and on scroll/resize
  // so the popover follows the input if the modal scrolls.
  useLayoutEffect(() => {
    if (!open) return undefined;
    function measure() {
      const el = inputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const MAX_H = 220;   // keep in sync with the popover's maxHeight below
      const spaceBelow = window.innerHeight - r.bottom;
      // Flip up only when below is too tight AND above has more room.
      const dropUp = spaceBelow < MAX_H && r.top > spaceBelow;
      setPopPos({
        left:   r.left,
        top:    r.bottom,
        bottom: window.innerHeight - r.top,   // viewport-relative for position:fixed
        width:  Math.max(r.width, 320),
        dropUp,
      });
    }
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, value, matches.length]);

  function commitPick(a) {
    const next = a.clientName
      ? `${a.clientCode} — ${a.clientName}`
      : a.clientCode;
    onChange && onChange(next);
    setOpen(false);
  }

  function onKeyDown(e) {
    if (!open || matches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commitPick(matches[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  const listVisible = open && (matches.length > 0 || showHint);

  return (
    <span
      ref={wrapRef}
      className="cg-anchor-picker"
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onFocus={(e) => { setOpen(true); onFocus && onFocus(e); }}
        onChange={(e) => { onChange && onChange(e.target.value); setOpen(true); }}
        onKeyDown={onKeyDown}
        style={{ width: 280, ...(inputStyle || {}) }}
        autoComplete="off"
      />
      {listVisible && (
        <div
          className="cg-anchor-picker-pop"
          role="listbox"
          style={{
            position:      'fixed',
            left:          popPos.left,
            width:         popPos.width,
            zIndex:        10000,
            background:    '#fff',
            border:        '1px solid #ccc',
            maxHeight:     220,
            overflowY:     'auto',
            fontSize:      13,
            ...(popPos.dropUp
              ? { bottom: popPos.bottom, borderBottom: 0, boxShadow: '0 -6px 14px rgba(0,0,0,0.18)' }
              : { top:    popPos.top,    borderTop:    0, boxShadow: '0 6px 14px rgba(0,0,0,0.18)' }),
          }}
        >
          {showHint && (
            <div
              className="cg-cell-hint"
              style={{ padding: '6px 10px', fontStyle: 'italic' }}
            >
              Type at least {minChars} characters…
            </div>
          )}
          {!showHint && matches.length === 0 && (
            <div
              className="cg-cell-hint"
              style={{ padding: '6px 10px', fontStyle: 'italic' }}
            >
              No matches.
            </div>
          )}
          {matches.map((a, i) => {
            const isHi = i === highlight;
            return (
              <div
                key={a.clientCode}
                role="option"
                aria-selected={isHi}
                onMouseDown={(e) => { e.preventDefault(); commitPick(a); }}
                onMouseEnter={() => setHighlight(i)}
                style={{
                  padding:    '6px 10px',
                  cursor:     'pointer',
                  background: isHi ? '#e9f3f1' : 'transparent',
                  whiteSpace: 'nowrap',
                  overflow:   'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={
                  a.via
                    ? `${a.via.clientCode}${a.via.clientName ? ` — ${a.via.clientName}` : ''} is in the Relationship Group anchored on ${a.clientCode}${a.clientName ? ` — ${a.clientName}` : ''}`
                  : a.isOrphan
                    ?`${a.clientCode}${a.clientName ? ` — ${a.clientName}` : ''} — ungrouped; selecting starts a new Relationship Group anchored on this client`
                    : (a.clientName ? `${a.clientCode} — ${a.clientName}` : a.clientCode)
                }
              >
                {a.via && (
                  <span style={{ marginRight: 6, fontSize: 12, color: '#555' }}>
                    <ClientChip code={a.via.clientCode} name={a.via.clientName} /> is in the group of
                  </span>
                )}
                <ClientChip code={a.clientCode} name={a.clientName} />
                {a.isOrphan && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 11,
                      color: '#6a4a00',
                      background: '#fff8e6',
                      border: '1px solid #f0d69a',
                      borderRadius: 3,
                      padding: '0 5px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    ungrouped · starts new RG
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </span>
  );
}
